import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccessControl } from '@/contexts/AccessContext';
import {
  DEFAULT_ROUTE,
  isFeatureFlaggedPath,
  isPathAccessible,
  resolveRoleFromCode,
} from '@/utils/routeAccess';

/**
 * Keeps the active route aligned with the selected role.
 * Redirects when the current path is not available for the active role and
 * runs on role switch as well as initial load / refresh.
 *
 * Returns whether the current route may be rendered. It is false until the
 * first profile load finishes and while a redirect is pending, so a forbidden
 * page never flashes before the redirect.
 */
export const useRoleRouteSync = (): boolean => {
  const { role, snapshot, activeRoleCode, isLoading, profile } = useAccessControl();
  const location = useLocation();
  const navigate = useNavigate();

  // Wait for the first profile load; role switches set activeRoleCode immediately. Feature-flag
  // gated routes wait regardless, since their flags only arrive with the profile and a missing
  // flag would otherwise redirect a deep link before the flag is known.
  const isResolving = isLoading && (!activeRoleCode || isFeatureFlaggedPath(location.pathname));
  const isAccessible =
    !isResolving &&
    isPathAccessible(location.pathname, {
      role: resolveRoleFromCode(activeRoleCode, role),
      snapshot,
      skipPermissionCheck: isLoading,
      featureFlags: profile?.featureFlags,
    });

  useEffect(() => {
    if (isResolving || isAccessible) return;
    navigate(DEFAULT_ROUTE, { replace: true });
  }, [isResolving, isAccessible, navigate]);

  return isAccessible;
};
